import React, { useCallback, useState } from "react"

import { CalendarRange, Clock, FolderOpen, List } from "lucide-react"

import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { useAuth } from "@/contexts/AuthContext"

import DirectoryView from "./planning/DirectoryView"
import PlanningListView from "./planning/PlanningListView"
import PlanningTimelineView from "./planning/PlanningTimelineView"
import WeeklyPlanView from "./planning/WeeklyPlanView"

type PlanningTab = "timeline" | "list" | "weekly-plan" | "directory"

export interface EditRouteNavigation {
  date: Date
  routeId: string
  editMode?: "instance" | "planned"
}

const PlanningContent: React.FC = () => {
  const { can } = useAuth()
  const [activeSubTab, setActiveSubTab] = useState<PlanningTab>("timeline")
  const [editRouteNavigation, setEditRouteNavigation] = useState<EditRouteNavigation | null>(null)

  const navigateToTimelineForEdit = useCallback(
    (date: Date, routeId: string, editMode: "instance" | "planned" = "planned") => {
      setEditRouteNavigation({ date, routeId, editMode })
      setActiveSubTab("timeline")
    },
    [],
  )

  const clearEditNavigation = useCallback(() => {
    setEditRouteNavigation(null)
  }, [])

  return (
    <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
      <div className="bg-white border-b border-palette-jungleGreen200 px-6 py-1.5 flex-shrink-0">
        <Tabs value={activeSubTab} onValueChange={(v) => setActiveSubTab(v as PlanningTab)}>
          <TabsList className="bg-transparent h-8 p-0 gap-0.5">
            <TabsTrigger
              value="timeline"
              className="data-[state=active]:bg-palette-jungleGreen100 data-[state=active]:text-palette-jungleGreen900 text-palette-jungleGreen500 gap-1.5 px-3 py-1 rounded text-xs font-medium"
            >
              <Clock size={12} />
              Timeline
            </TabsTrigger>
            <TabsTrigger
              value="list"
              className="data-[state=active]:bg-palette-jungleGreen100 data-[state=active]:text-palette-jungleGreen900 text-palette-jungleGreen500 gap-1.5 px-3 py-1 rounded text-xs font-medium"
            >
              <List size={12} />
              List
            </TabsTrigger>
            {/* HIVED-only; nothing else selects these tabs, so omitting the triggers suffices. */}
            {can.allBoards && (
              <>
                <TabsTrigger
                  value="weekly-plan"
                  className="data-[state=active]:bg-palette-jungleGreen100 data-[state=active]:text-palette-jungleGreen900 text-palette-jungleGreen500 gap-1.5 px-3 py-1 rounded text-xs font-medium"
                >
                  <CalendarRange size={12} />
                  Weekly Plan
                </TabsTrigger>
                <TabsTrigger
                  value="directory"
                  className="data-[state=active]:bg-palette-jungleGreen100 data-[state=active]:text-palette-jungleGreen900 text-palette-jungleGreen500 gap-1.5 px-3 py-1 rounded text-xs font-medium"
                >
                  <FolderOpen size={12} />
                  Directory
                </TabsTrigger>
              </>
            )}
          </TabsList>
        </Tabs>
      </div>

      <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
        {activeSubTab === "timeline" && (
          <PlanningTimelineView
            editNavigation={editRouteNavigation}
            onClearEditNavigation={clearEditNavigation}
          />
        )}
        {activeSubTab === "list" && (
          <PlanningListView onNavigateToEdit={navigateToTimelineForEdit} />
        )}
        {activeSubTab === "weekly-plan" && <WeeklyPlanView />}
        {activeSubTab === "directory" && <DirectoryView />}
      </div>
    </div>
  )
}

export default PlanningContent
